import { useEffect } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import config from "@/config";
import { lenderConfigAtom } from "@/recoil/atom/lenderConfigAtom";
import {
  DEFAULT_LENDER_PROFILES,
  DEFAULT_PRIMARY_LENDER,
} from "@/lib/lenderDefaults";
import { resolveLender } from "@/lib/lenderResolveCore";

// Resolution + consent copy live in lenderResolveCore.js (dependency-free, node-tested).
export {
  buildConsents,
  isNeutralLender,
  lenderConsentLabel,
  lenderNamePhrase,
  resolveLender,
  NEUTRAL_LENDER_LABEL,
} from "@/lib/lenderResolveCore";

/**
 * Returns the resolved lender profile (branding + consents) for a lender key.
 * Pass the journey/user lender (e.g. `userData.lender`); omit to get the
 * configured primary lender (for pre-journey screens). A journey lender with no usable
 * identity yields the NEUTRAL profile (`isNeutralLender`), never another lender.
 */
export function useLender(key) {
  const lenderConfig = useRecoilValue(lenderConfigAtom);
  return resolveLender(lenderConfig, key);
}

/**
 * The lender's IDENTITY only — legalName, shortCode, logoUrl, homeUrl — for the key given,
 * with no fallback to the primary lender at all: an unknown or missing key yields `null`
 * (where `useLender` would hand back the primary lender for a missing key, or the neutral
 * profile for an unusable one). Use this wherever the screen is
 * already committed to one lender and rendering any other lender would be wrong
 * (E-Sign, KFS, the Gromor consent dialog).
 */
export function useLenderIdentity(key) {
  const lenderConfig = useRecoilValue(lenderConfigAtom);
  const lenders = lenderConfig?.lenders || DEFAULT_LENDER_PROFILES;
  const profile = (key && (lenders[key] || DEFAULT_LENDER_PROFILES[key])) || null;
  return profile?.legalName ? profile : null;
}

/**
 * Fetches the live lender config once and overlays it on the defaults.
 * Non-blocking and failure-safe — the UI keeps the defaults if it can't load.
 * Call once near the app root.
 */
export function useLoadLenderConfig() {
  const setConfig = useSetRecoilState(lenderConfigAtom);

  useEffect(() => {
    let active = true;
    fetch(`${config.API_URL}/public/lender-config`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!active || !data?.lenders) return;
        const merged = { ...DEFAULT_LENDER_PROFILES };
        for (const k of Object.keys(data.lenders)) {
          merged[k] = { ...DEFAULT_LENDER_PROFILES[k], ...data.lenders[k] };
        }
        setConfig({
          primaryLender: data.primaryLender || DEFAULT_PRIMARY_LENDER,
          lenders: merged,
        });
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [setConfig]);
}
